import moment, {Moment} from 'moment';
import {CycleResource} from '@services/resource/cycle-resource';
import {Ord, ordNumber} from 'fp-ts/lib/Ord';
import {Eq} from 'fp-ts/lib/Eq';

export class Cycle {
  constructor(
    public readonly uuid: string | null,
    public readonly name: string,
    public readonly description: string,
    public readonly location: string,
    public readonly applicationStart: Moment,
    public readonly applicationEnd: Moment,
    public readonly votingStart: Moment,
    public readonly votingEnd: Moment,
    public readonly feedback: Moment,
    public readonly pitchDate: Moment, // the other moments are ISO-formatted UTC timestamps, this one is only a date (no time)
    public readonly publishStart: Moment,
    public readonly publishEnd: Moment,
  ) {}

  public static fromResource(r: CycleResource): Cycle {
    return new Cycle(
      r.uuid,
      r.name,
      r.description,
      r.location,
      moment.utc(r.applicationStart),
      moment.utc(r.applicationEnd),
      moment.utc(r.votingStart),
      moment.utc(r.votingEnd),
      moment.utc(r.feedback),
      moment(r.pitchDate), // see comment above, pitchDate is nominal, e.g. it's not UTC
      moment.utc(r.publishStart),
      moment.utc(r.publishEnd),
    );
  }

  public static toResource(c: Cycle): CycleResource {
    return {
      uuid: c.uuid,
      name: c.name,
      description: c.description,
      location: c.location,
      applicationStart: c.applicationStart.utc().toISOString(),
      applicationEnd: c.applicationEnd.utc().toISOString(),
      votingStart: c.votingStart.utc().toISOString(),
      votingEnd: c.votingEnd.utc().toISOString(),
      feedback: c.feedback.utc().toISOString(),
      pitchDate: c.pitchDate.format('YYYY-MM-DD'), // ISOString() is wrong here
      publishStart: c.publishStart.utc().toISOString(),
      publishEnd: c.publishEnd.utc().toISOString(),
    };
  }
}

export const eqCycle: Eq<Cycle> = {
  equals: (x, y) => x.uuid === y.uuid,
};

export const ordApplicationEnd: Ord<Cycle> = {
  equals: (c1: Cycle, c2: Cycle) => ordNumber.equals(c1.applicationEnd.milliseconds(), c2.applicationEnd.milliseconds()),
  compare: (c1: Cycle, c2: Cycle) => ordNumber.compare(c1.applicationEnd.milliseconds(), c2.applicationEnd.milliseconds()),
};

export const ordApplicationStart: Ord<Cycle> = {
  equals: (c1: Cycle, c2: Cycle) => ordNumber.equals(c1.applicationStart.milliseconds(), c2.applicationStart.milliseconds()),
  compare: (c1: Cycle, c2: Cycle) => ordNumber.compare(c1.applicationStart.milliseconds(), c2.applicationStart.milliseconds()),
};
